import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Icon, InlineText, MiniLoader } from '@pwc/pwc-deals-ui';
import { Constants, TextUtilities } from '@pwc/pwc-deals-web-common';
import UserViewStatusPill from 'common/components/Pills/UserViewStatusPill';

import ReportLayoutButton from 'common/components/ReportLayoutIcon/ReportLayoutButton';
import ApprovalPill from 'common/components/Pills/ApprovalPill';
import { DraftPillClientView } from 'common/components/Pills/DraftPill';
import featureFlags from 'common/helpers/featureFlags';
import VersionStatus from 'common/components/Pills/VersionStatus';
import ShowForPermissions from 'features/security/ShowForPermissions';
import ShowForRole from 'features/security/ShowForRole';
import EnableForProjectState from 'features/security/EnableForProjectState';
import './TopicHeader.scss';
import PageAuditFields from 'common/components/PageAuditFields/PageAuditFields';
import { useSelector } from 'react-redux';
import { getIsThirdPartyMember } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { selectTopicSavingState } from '../../../../../../common/redux/project-overview/projectOverviewSlice';
import AvatarWithMenu from '../../../../../../common/components/AvatarWithMenu/index.tsx';
import TopicActionPanel from './TopicActionPanel.tsx';

const TopicHeader = ({
  onDelete,
  onEdit,
  onChange,
  topic,
  isLoading,
  topicLayout,
  workflowActions,
  viewClient,
  onMove,
  moveOption,
  handleOpenPanel,
  isEditMode = false,
  topicError = false,
  topicErrorMessage = '',
}) => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const {
    isSaving = false,
    isSavingFulfilled = false,
    isSavingRejected = false,
  } = useSelector(selectTopicSavingState(topic.content.contentThreadId));
  const [title, setTitle] = useState(topic.content.title);

  function movePrev() {
    onMove(-1);
  }

  const moveNext = () => {
    onMove(1);
  };

  const disableMovePrev = () => {
    return (
      isSaving ||
      (moveOption !== Constants.MOVE_OPTIONS.PREV &&
        moveOption !== Constants.MOVE_OPTIONS.BOTH) ||
      (!featureFlags.isAutoSaveEnabled && topic.isDraftTopic)
    );
  };

  const disableMoveNext = () => {
    return (
      isSaving ||
      (moveOption !== Constants.MOVE_OPTIONS.NEXT &&
        moveOption !== Constants.MOVE_OPTIONS.BOTH) ||
      (!featureFlags.isAutoSaveEnabled && topic.isDraftTopic)
    );
  };

  const handleOnChange = (event) => {
    setTitle(event.target.value);
    onChange(event.target.value);
  };

  const handleBlur = () => {
    const trimmed = TextUtilities.trimText(topic.content.title);
    if (trimmed !== topic.content.title) {
      onChange(trimmed);
    }
  };

  const savingIndicator = (
    <div className="saving">
      <MiniLoader monochromatic />
      <div className="message">{t('saving')}</div>
    </div>
  );

  const savedIndicator = (
    <div className="saving">
      <Icon name="confirmed" className="outline icon-green" />
      <div className="message">Saved</div>
    </div>
  );

  const savingRejectedIndicator = (
    <div className="saving">
      <Icon name="alert" className="fill icon-pink saving-error" />
      <div className="message">Saving failed</div>
    </div>
  );

  return (
    <div className="topic-top-bar-container">
      {isEditMode ? (
        <div className="title-container">
          <InlineText
            field={{
              name: 'title',
              value: title,
              onChange: handleOnChange,
            }}
            placeholder="Topic title (required)"
            onBlur={handleBlur}
            error={topicError ? topicErrorMessage : undefined}
          />
        </div>
      ) : (
        <h3 className="topic-title" data-testid="topic-title">
          {topic.content.title}
        </h3>
      )}
      {!isThirdPartyMember && (
        <div className="pills-section">
          <ShowForRole
            projectId={projectId}
            roles={Constants.USER_ROLE_GROUPS.INTERNAL}
          >
            <VersionStatus content={topic.content} allowDraftPill />
          </ShowForRole>
          <ShowForRole
            projectId={projectId}
            roles={Constants.USER_ROLE_GROUPS.CLIENT}
          >
            <DraftPillClientView />
          </ShowForRole>
          <ApprovalPill versionStatus={topic.content.versionStatus} />
          <UserViewStatusPill userViewStatus={topic.content.userViewStatus} />
        </div>
      )}
      <ShowForRole
        projectId={projectId}
        roles={Constants.USER_ROLE_GROUPS.INTERNAL}
      >
        {topic.content.dateModified ? (
          <PageAuditFields
            dateModified={topic.content.dateModified}
            modifiedBy={topic.content.modifiedBy}
            shareWithAll={
              topic.content.versionStatus ===
              Constants.VERSION_STATUS.SHARED.value
            }
            version={topic.content.versionNumber}
          />
        ) : (
          ''
        )}
      </ShowForRole>
      <ShowForPermissions
        projectId={projectId}
        permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
      >
        <EnableForProjectState projectId={projectId}>
          {({ isProjectDisabled }) => (
            <>
              <div className="topic-right-panel">
                {featureFlags.isAutoSaveEnabled &&
                  isEditMode &&
                  isSaving &&
                  savingIndicator}
                {featureFlags.isAutoSaveEnabled &&
                  isEditMode &&
                  !isSaving &&
                  isSavingFulfilled &&
                  savedIndicator}
                {featureFlags.isAutoSaveEnabled &&
                  isEditMode &&
                  !isSaving &&
                  isSavingRejected &&
                  savingRejectedIndicator}
                {isEditMode && (
                  <ReportLayoutButton
                    onClick={() => handleOpenPanel(topicLayout)}
                    layoutType={topicLayout}
                  />
                )}
                <button
                  className="topic-move-button"
                  data-testid="topic-move-button-prev"
                  type="button"
                  onClick={movePrev}
                  disabled={isProjectDisabled || disableMovePrev()}
                >
                  <Icon
                    name="move-up"
                    width={24}
                    className={`outline ${
                      isProjectDisabled || disableMovePrev()
                        ? 'icon-grey-medium'
                        : 'icon-orange-theme'
                    }`}
                  />
                </button>
                <button
                  className="topic-move-button"
                  data-testid="topic-move-button-next"
                  type="button"
                  onClick={moveNext}
                  disabled={isProjectDisabled || disableMoveNext()}
                >
                  <Icon
                    name="move-down"
                    width={24}
                    className={`outline ${
                      isProjectDisabled || disableMoveNext()
                        ? 'icon-grey-medium'
                        : 'icon-orange-theme'
                    }`}
                  />
                </button>
                {isEditMode && (
                  <>
                    <button
                      className="topic-delete-button"
                      data-testid="topic-delete-button-next"
                      type="button"
                      onClick={onDelete}
                      disabled={isProjectDisabled}
                    >
                      <Icon
                        name="delete"
                        width={24}
                        className={`outline ${
                          isProjectDisabled
                            ? 'icon-grey-medium'
                            : 'icon-orange-theme'
                        }`}
                      />
                    </button>
                  </>
                )}
                {!isEditMode && (
                  <>
                    <AvatarWithMenu
                      fixedWidth={false}
                      contentThreadId={topic.content.contentThreadId}
                    >
                      <TopicActionPanel
                        onEdit={onEdit}
                        onDelete={onDelete}
                        projectId={projectId}
                        topic={topic}
                        workflowActions={workflowActions}
                        viewClient={viewClient}
                        isProjectDisabled={isProjectDisabled}
                        isLoading={isLoading}
                      />
                    </AvatarWithMenu>
                  </>
                )}
              </div>
              <div
                className={`global-error-placeholder-${topic.content.contentThreadId}`}
              />
            </>
          )}
        </EnableForProjectState>
      </ShowForPermissions>
    </div>
  );
};

TopicHeader.propTypes = {
  onDelete: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  topic: PropTypes.object.isRequired,
};

TopicHeader.defaultProps = {};

export default TopicHeader;
